import React, { useState } from "react";
import { RichTextEditor } from "../EmailTemplateEditor/RichTextEditor";
import { Button } from "../ui/button";
import { Eye, EyeOff } from "react-feather";
import {
  checkRichTextLength,
  getRichTextLimitError,
} from "@/utils/richText";

// Function to process content and convert YouTube links to iframes
function processYouTubeContent(content: string): string {
  if (!content) return content;

  // First, handle YouTube links that are wrapped in <a> tags by TipTap
  let processedContent = content.replace(
    /<a[^>]*href="(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+))"[^>]*>.*?<\/a>/g,
    (_, _url, videoId) => {
      const embedUrl = `https://www.youtube.com/embed/${videoId}`;
      return `<div class="youtube-embed-wrapper"><iframe width="100%" height="315" src="${embedUrl}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>`;
    }
  );

  // Then handle any plain YouTube URLs that might not be in links
  processedContent = processedContent.replace(
    /(?<!href=["'])https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(?!["'])/g,
    (_, videoId) => {
      const embedUrl = `https://www.youtube.com/embed/${videoId}`;
      return `<div class="youtube-embed-wrapper"><iframe width="100%" height="315" src="${embedUrl}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>`;
    }
  );

  // Also wrap existing iframes in our wrapper for consistent styling
  processedContent = processedContent.replace(
    /<iframe([^>]*youtube[^>]*)><\/iframe>/g,
    '<div class="youtube-embed-wrapper"><iframe$1></iframe></div>'
  );

  return processedContent;
}

type AudienceTarget = "AGENCY" | "AFFILIATE" | "BRAND";
type Priority = number; // Higher number = higher importance

// Helper function to get priority label
const getPriorityLabel = (priority: Priority): string => {
  if (priority >= 1000) return "Very High";
  if (priority >= 100) return "High";
  if (priority >= 10) return "Medium";
  if (priority >= 1) return "Low";
  return "Lowest";
};

interface FAQ {
  question: string;
  answer: string;
  audienceTargets: AudienceTarget[];
  priority: Priority;
}

interface AdminFaqFormProps {
  onSubmit?: (faq: FAQ) => void;
  initialData?: FAQ;
  isLoading?: boolean;
}

// YouTube embed styles
const youtubeEmbedStyles = `
  .youtube-embed-wrapper {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%; /* 16:9 aspect ratio */
    height: 0;
    margin: 12px 0 !important;
    overflow: hidden;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
  
  .youtube-embed-wrapper iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100% !important;
    height: 100% !important;
    border: none !important;
    border-radius: 8px;
  }
  
  @media (max-width: 640px) {
    .youtube-embed-wrapper {
      padding-bottom: 65%; /* Slightly taller on mobile for better viewing */
      margin: 8px 0 !important;
    }
  }
`;

export default function AdminFaqForm({
  onSubmit,
  initialData = {
    question: "",
    answer: "",
    audienceTargets: [],
    priority: 10 as Priority, // Default medium priority
  },
  isLoading = false,
}: AdminFaqFormProps) {
  const [faq, setFaq] = useState<FAQ>(initialData);
  const [showPreview, setShowPreview] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!faq.question.trim()) {
      alert("Please enter a question");
      return;
    }

    if (!faq.answer.trim()) {
      alert("Please enter an answer");
      return;
    }

    const answerError = getRichTextLimitError(faq.answer, 2000);
    if (answerError) {
      alert(answerError);
      return;
    }

    if (faq.audienceTargets.length === 0) {
      alert("Please select at least one audience target");
      return;
    }

    if (faq.priority < 0) {
      alert("Priority must be a positive number");
      return;
    }

    onSubmit?.(faq);
  };

  const handleQuestionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFaq((prev) => ({
      ...prev,
      question: e.target.value,
    }));
  };

  const handleAnswerChange = (content: string) => {
    setFaq((prev) => ({
      ...prev,
      answer: content,
    }));
  };

  const handleAudienceTargetChange = (target: AudienceTarget) => {
    setFaq((prev) => ({
      ...prev,
      audienceTargets: prev.audienceTargets.includes(target)
        ? prev.audienceTargets.filter((t) => t !== target)
        : [...prev.audienceTargets, target],
    }));
  };

  const handlePriorityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFaq((prev) => ({
      ...prev,
      priority: value === "" ? 0 : parseInt(value) || 0,
    }));
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border">
      <style>{youtubeEmbedStyles}</style>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">
            {initialData.question
              ? `Edit FAQ (Priority: ${getPriorityLabel(faq.priority)})`
              : "Create New FAQ"}
          </h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-2"
          >
            {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
            {showPreview ? "Hide Preview" : "Show Preview"}
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Question Field */}
          <div>
            <label
              htmlFor="question"
              className="block text-sm font-medium text-muted-foreground mb-2"
            >
              Question *
            </label>
            <input
              id="question"
              type="text"
              value={faq.question}
              onChange={handleQuestionChange}
              placeholder="Enter your FAQ question here..."
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d7785b]/30 focus:border-transparent"
              required
            />
          </div>

          {/* Audience Target Field */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Audience Target *
            </label>
            <div className="space-y-2">
              {(["AGENCY", "AFFILIATE", "BRAND"] as AudienceTarget[]).map(
                (target) => (
                  <label
                    key={target}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={faq.audienceTargets.includes(target)}
                      onChange={() => handleAudienceTargetChange(target)}
                      className="h-4 w-4 text-[#d7785b] border-border rounded focus:ring-[#d7785b]/30"
                    />
                    <span className="text-sm text-muted-foreground capitalize">
                      {target}
                    </span>
                  </label>
                )
              )}
            </div>
            {faq.audienceTargets.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {faq.audienceTargets.map((target) => (
                  <span
                    key={target}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300"
                  >
                    {target}
                    <button
                      type="button"
                      onClick={() => handleAudienceTargetChange(target)}
                      className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-[#d7785b] hover:bg-orange-200 dark:hover:bg-orange-500/25"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Priority Field */}
          <div>
            <label
              htmlFor="priority"
              className="block text-sm font-medium text-muted-foreground mb-2"
            >
              Priority (Higher number = Higher importance)
            </label>
            <div className="relative">
              <input
                id="priority"
                type="number"
                min="0"
                step="1"
                value={faq.priority}
                onChange={handlePriorityChange}
                placeholder="e.g., 10, 100, 9352"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d7785b]/30 focus:border-transparent"
              />
              <div className="mt-1 text-xs text-muted-foreground">
                Current: {getPriorityLabel(faq.priority)} ({faq.priority})
              </div>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Examples: 1-9 (Low), 10-99 (Medium), 100-999 (High), 1000+ (Very
              High)
            </div>
          </div>

          {/* Answer Field */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Answer *
            </label>
            {showPreview ? (
              <div className="space-y-4">
                <div className="border border-border rounded-lg">
                  <div className="bg-muted px-4 py-2 border-b border-border">
                    <h4 className="text-sm font-medium text-muted-foreground">
                      HTML Preview
                    </h4>
                  </div>
                  <div
                    className="p-4 prose max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: processYouTubeContent(faq.answer),
                    }}
                  />
                </div>
                <RichTextEditor
                  content={faq.answer}
                  onChange={handleAnswerChange}
                  placeholder="Enter your FAQ answer here. You can use formatting tools above."
                  maxLength={2000}
                />
              </div>
            ) : (
              <RichTextEditor
                content={faq.answer}
                onChange={handleAnswerChange}
                placeholder="Enter your FAQ answer here. You can use formatting tools above."
                maxLength={2000}
              />
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4">
            <Button
              type="submit"
              disabled={
                isLoading ||
                !faq.question.trim() ||
                !faq.answer.trim() ||
                faq.audienceTargets.length === 0 ||
                checkRichTextLength(faq.answer, 2000).isOver
              }
              className="bg-[#d7785b] hover:bg-[#c86c51] text-white px-6 py-2"
            >
              {isLoading
                ? "Saving..."
                : initialData.question
                ? "Update FAQ"
                : "Create FAQ"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
